/**
 * ExportButton.js
 * Reusable export button component with dropdown for PDF and Excel formats
 *
 * Props:
 * - onExportPDF: Function to call when PDF export is clicked
 * - onExportExcel: Function to call when Excel export is clicked
 * - title: Button title/tooltip (optional, default: 'Export data')
 * - disabled: Whether the button is disabled (optional, default: false)
 * - className: Additional CSS classes (optional)
 *
 * Usage:
 * <ExportButton
 *   onExportPDF={() => handlePDFExport()}
 *   onExportExcel={() => handleExcelExport()}
 *   title="Export maintenance data"
 * />
 */

import React, { useRef, useEffect } from 'react';

const ExportButton = ({
  onExportPDF,
  onExportExcel,
  title = 'Export data',
  disabled = false,
  className = '',
  excelOnly = false,
}) => {
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        const menu = menuRef.current.querySelector('.export-dropdown-menu');
        if (menu) {
          menu.style.display = 'none';
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = (e) => {
    e.stopPropagation();
    const menu = menuRef.current.querySelector('.export-dropdown-menu');
    if (menu) {
      const isVisible = menu.style.display !== 'none';
      menu.style.display = isVisible ? 'none' : 'block';
    }
  };

  const handlePDFClick = () => {
    onExportPDF();
    const menu = menuRef.current.querySelector('.export-dropdown-menu');
    if (menu) {
      menu.style.display = 'none';
    }
  };

  const handleExcelClick = () => {
    onExportExcel();
    const menu = menuRef.current?.querySelector('.export-dropdown-menu');
    if (menu) {
      menu.style.display = 'none';
    }
  };

  const handleExcelOnlyClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      onExportExcel();
    }
  };

  return (
    <div
      ref={menuRef}
      style={{
        position: 'relative',
        display: 'inline-block'
      }}
      className={className}
    >
      {/* Export button */}
      <button
        onClick={excelOnly ? handleExcelOnlyClick : toggleMenu}
        title={title}
        disabled={disabled}
        style={{
          background: 'none',
          border: 'none',
          padding: '0.25rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '1.5rem',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.5 : 1,
          lineHeight: '1'
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.transform = 'scale(1.15)';
          }
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
        }}
      >
        📥
      </button>

      {/* Dropdown menu */}
      {!excelOnly && (
      <div
        className="export-dropdown-menu"
        style={{
          display: 'none',
          position: 'absolute',
          right: 0,
          top: '100%',
          marginTop: '0.5rem',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 100,
          minWidth: '180px',
          overflow: 'hidden'
        }}
      >
        {/* Excel Export Option */}
        <button
          onClick={handleExcelClick}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.75rem 1rem',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '0.9rem',
            color: '#333',
            transition: 'background-color 0.2s',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          title="Export as Excel"
        >
          📊 Export as Excel
        </button>

        {/* Divider */}
        <div style={{
          height: '1px',
          backgroundColor: '#eee',
          margin: '0'
        }} />

        {/* PDF Export Option */}
        <button
          onClick={handlePDFClick}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.75rem 1rem',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '0.9rem',
            color: '#333',
            transition: 'background-color 0.2s',
            fontWeight: '500'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
          title="Export as PDF"
        >
          📄 Export as PDF
        </button>
      </div>
      )}
    </div>
  );
};

export default ExportButton;
